import { COLUMN_NAME_ID } from '../../../constants';

export const AdColumn = Object.freeze({
  Alert: 'Alert',
  Name: COLUMN_NAME_ID,
  EditAd: 'EditAd',
  TrackingId: 'TrackingId',
  DeleteAd: 'deleteAd',
  AdID: 'adID',
  Countries: 'Countries',
  Country: 'Countries',
  Type: 'Type',
  Status: 'Status',
  Review: 'Review',
  ScheduleStart: 'ScheduleStart',
  ScheduleEnd: 'ScheduleEnd',
  LegalApprovedCreative: 'legalApprovedCreative',
  DisplayPublisherTarget: 'DisplayPublisherTarget',
  ScheduleTimezoneOffset: 'ScheduleTimezoneOffset',
  Weight: 'Weight',
  ViewabilityVendorList: 'ViewabilityVendorList',
  FrequencyCapList: 'frequencyCapList',
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  LineItemName: 'LineItemName',
  LineItemId: 'lineItemId',
  CampaignName: 'CampaignName',
  CampaignId: 'campaignId',
  RotationName: 'RotationName',
  SequenceId: 'SequenceId',
  SequenceName: 'SequenceName',
  SequenceOrder: 'SequenceOrder',
  PodPosition: 'PodPosition',
  SlotPosition: 'SlotPosition',
});

export const AdColumnMeta: Record<string, { header: string; tooltip?: string; subHeader?: string }> = {
  [AdColumn.Alert]: {
    header: 'Alert',
    tooltip: 'An icon in this column indicates an issue with the ad. Hover over the icon for more information.',
  },
  [AdColumn.Name]: {
    header: 'Ad Name',
    tooltip: 'The name entered when the ad was created in Mission Control.',
  },
  [AdColumn.EditAd]: {
    header: 'Edit Ad',
    tooltip: 'Click the pencil icon to modify ad properties, such as creative assets, tags, targeting, and schedule.',
  },
  [AdColumn.TrackingId]: {
    header: 'Tracking ID',
    tooltip:
      '16 digit identifier for the ad used for external purposes and shared with 3rd-party vendors, gets passed via our macros.',
  },
  [AdColumn.DeleteAd]: {
    header: 'Delete Ad',
  },
  [AdColumn.AdID]: {
    header: 'Ad ID',
    tooltip:
      '36 character identifier used when communicating with internal teams, for example when creating a JIRA ticket to report an issue.',
  },
  [AdColumn.Countries]: {
    header: 'Country',
    tooltip: 'The countries where the ad is eligible to serve.',
  },
  [AdColumn.Type]: {
    header: 'Ad Type',
    tooltip: 'Format allowed under the ad product.',
  },
  [AdColumn.Status]: {
    header: 'Status',
    tooltip: 'Indicates whether the ad is active or not.',
  },
  [AdColumn.Review]: {
    header: 'Review',
    tooltip: 'Campaign review status for the ad.',
  },
  [AdColumn.ScheduleStart]: {
    header: 'Start Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip: 'The date the ad is first eligible for delivery.',
  },
  [AdColumn.ScheduleEnd]: {
    header: 'End Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip: 'The last date the ad is eligible for delivery.',
  },
  [AdColumn.LegalApprovedCreative]: {
    header: 'Legal-Approved Creative',
    tooltip:
      'A Yes value in this column indicates that the ad has already been approved by the legal department. There is a toggle in the Create/Edit Ad workflow account managers can set to True or False.',
  },
  [AdColumn.DisplayPublisherTarget]: {
    header: 'Publisher',
    tooltip: 'The name of the app that will run the ad.',
  },
  [AdColumn.ScheduleTimezoneOffset]: {
    header: 'Time Zone',
    tooltip: 'The time zone for the ad date range.',
  },
  [AdColumn.Weight]: {
    header: 'Weight',
    tooltip: 'Value for an ad within a rotation group that determines priority for how often it is served.',
  },
  [AdColumn.ViewabilityVendorList]: {
    header: 'Viewability Vendor',
    tooltip: 'The third-party vendor whose data we use to confirm if ads were viewable.',
  },
  [AdColumn.FrequencyCapList]: {
    header: 'Frequency Cap',
    tooltip: 'The limit for how many times an ad can be served within a given timeframe.',
  },
  [AdColumn.CreatedAt]: {
    header: 'Created Date',
    tooltip: 'The date the ad was first created.',
  },
  [AdColumn.UpdatedAt]: {
    header: 'Updated Date',
    tooltip: 'The most recent date changes were applied to the ad.',
  },
  [AdColumn.LineItemName]: {
    header: 'Line Item Name',
    tooltip: 'The name of the line item the ad belongs to.',
  },
  [AdColumn.LineItemId]: {
    header: 'Line Item ID',
    tooltip: 'External ID for the line item shared with third-party vendors and populated in tag macros.',
  },
  [AdColumn.CampaignName]: {
    header: 'Campaign Name',
    tooltip: 'The name of the campaign the ad belongs to.',
  },
  [AdColumn.CampaignId]: {
    header: 'Campaign ID',
    tooltip: 'Internal UUID used for troubleshooting. Should not be shared externally.',
  },
  [AdColumn.RotationName]: {
    header: 'Rotation Group Name',
    tooltip: 'User-defined name for a collection of ads that will be served based on relative weighted values.',
  },
  [AdColumn.SequenceId]: {
    header: 'Sequence ID',
    tooltip:
      'System-generated identifier for the sequence the ad belongs to. This column will only display a value if the ad is part of a sequence.',
  },
  [AdColumn.SequenceName]: {
    header: 'Sequence Name',
    tooltip:
      'User-entered value identifying the sequence. This column will only display a value if the ad is part of a sequence.',
  },
  [AdColumn.SequenceOrder]: {
    header: 'Sequence #',
    tooltip:
      'The order in which the ad appears relative to other ads in the sequence. This column will only display a value if the ad is part of a sequence.',
  },
  [AdColumn.PodPosition]: {
    header: 'Pod',
    tooltip:
      'The specific pod assigned to the ad within a sequence. This column will only display a value if the ad is part of a sequence.',
  },
  [AdColumn.SlotPosition]: {
    header: 'Start or End of Pod',
    tooltip:
      'The start or end position assigned to the ad within a sequence. This column will only display a value if the ad is part of a sequence.',
  },
};
